/**
 * API layer for the user-management page: fetchers plus TanStack Query/Mutation
 * option factories. Talks to a single API (application/user-api), unlike the
 * room-booking-management api/ layer which juggles multiple API base URLs.
 */

import axios from "axios";

// mirrors LocalAccountFilter::DOMAIN / WwuAccountFilter::DOMAIN on the backend
export const LOCAL_DOMAIN = "mhs.uni-muenster.de";
export const WWU_DOMAIN = "uni-muenster.de";

let apiUrl = null;

export const initApiUrl = (url) => {
	apiUrl = url;
};

const buildUrl = (endpoint) => {
	if (!apiUrl) {
		throw new Error("User API URL not initialized");
	}
	return `${apiUrl.replace(/\/$/, "")}${endpoint}`;
};

/**
 * Normalizes an axios error into a plain Error carrying the server's response
 * body (if any) so callers can branch on `.invalid`/`.messages`/`.blockers`.
 */
const rethrow = (error) => {
	// already normalized (e.g. by checkForInlineValidationError) - pass through as-is
	if (error.invalid !== undefined || error.blockers !== undefined) {
		throw error;
	}
	if (error.response) {
		const body = error.response.data || {};
		const normalized = new Error(body.error || `HTTP Error ${error.response.status}`);
		normalized.invalid = body.invalid === true;
		normalized.messages = body.messages || null;
		normalized.blockers = body.blockers || null;
		throw normalized;
	}
	if (error.request) {
		throw new Error("Netzwerkfehler: Keine Antwort vom Server");
	}
	throw new Error(error.message || "Unbekannter Fehler");
};

/**
 * The backend returns validation errors with HTTP 200 + { error, invalid: true,
 * messages }, mirroring RoomBookingManagement's convention - detect that here
 * so both paths (axios-thrown 4xx and 200-with-invalid) end up as the same
 * kind of thrown error for callers.
 */
const checkForInlineValidationError = (data) => {
	if (data && data.invalid === true) {
		const error = new Error(data.error || "Fehler bei der Validierung der Eingabedaten");
		error.invalid = true;
		error.messages = data.messages || null;
		throw error;
	}
	return data;
};

export const fetchUsers = async () => {
	try {
		const response = await axios.get(buildUrl("/list"));
		return checkForInlineValidationError(response.data).list || [];
	} catch (error) {
		return rethrow(error);
	}
};

export const fetchRoles = async () => {
	try {
		const response = await axios.get(buildUrl("/roles"));
		return checkForInlineValidationError(response.data).list || [];
	} catch (error) {
		return rethrow(error);
	}
};

export const fetchUserDetail = async (id) => {
	try {
		const response = await axios.get(buildUrl("/detail"), { params: { id } });
		return checkForInlineValidationError(response.data);
	} catch (error) {
		return rethrow(error);
	}
};

export const createLocalAccount = async (payload) => {
	try {
		const response = await axios.post(buildUrl("/create-local"), payload);
		return checkForInlineValidationError(response.data);
	} catch (error) {
		return rethrow(error);
	}
};

export const createWwuAccount = async (payload) => {
	try {
		const response = await axios.post(buildUrl("/create-wwu"), payload);
		return checkForInlineValidationError(response.data);
	} catch (error) {
		return rethrow(error);
	}
};

export const updateUser = async (payload) => {
	try {
		const response = await axios.put(buildUrl("/update"), payload);
		return checkForInlineValidationError(response.data);
	} catch (error) {
		return rethrow(error);
	}
};

export const refreshFromLdap = async (id) => {
	try {
		const response = await axios.post(buildUrl("/refresh-ldap"), { id });
		return checkForInlineValidationError(response.data);
	} catch (error) {
		return rethrow(error);
	}
};

export const deleteUser = async (id) => {
	try {
		const response = await axios.delete(buildUrl("/delete"), { data: { id } });
		return checkForInlineValidationError(response.data);
	} catch (error) {
		return rethrow(error);
	}
};

export const queryKeys = {
	users: () => ["user-management", "users"],
	roles: () => ["user-management", "roles"],
	// deliberately NOT nested under users() - invalidating the list (e.g. after a
	// delete) must not cascade into other rows' still-open detail queries, nor
	// re-trigger the just-deleted row's own detail query (which would 404)
	userDetail: (id) => ["user-management", "user-detail", id],
};

export const useUsersQuery = () => ({
	queryKey: queryKeys.users(),
	queryFn: fetchUsers,
	staleTime: 1000 * 30,
	gcTime: 1000 * 60 * 10,
});

export const useRolesQuery = () => ({
	queryKey: queryKeys.roles(),
	queryFn: fetchRoles,
	staleTime: 1000 * 60 * 15,
	gcTime: 1000 * 60 * 60,
});

export const useUserDetailQuery = (id, enabled) => ({
	queryKey: queryKeys.userDetail(id),
	queryFn: () => fetchUserDetail(id),
	enabled,
	staleTime: 1000 * 15,
	gcTime: 1000 * 60 * 5,
});

export const useCreateLocalAccountMutation = (queryClient) => ({
	mutationFn: createLocalAccount,
	onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.users() }),
});

export const useCreateWwuAccountMutation = (queryClient) => ({
	mutationFn: createWwuAccount,
	onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.users() }),
});

export const useUpdateUserMutation = (queryClient) => ({
	mutationFn: updateUser,
	onSuccess: (data) => {
		queryClient.invalidateQueries({ queryKey: queryKeys.users() });
		if (data?.user?.id) {
			queryClient.invalidateQueries({ queryKey: queryKeys.userDetail(data.user.id) });
		}
	},
});

export const useRefreshFromLdapMutation = (queryClient) => ({
	mutationFn: refreshFromLdap,
	onSuccess: (data) => {
		queryClient.invalidateQueries({ queryKey: queryKeys.users() });
		if (data?.user?.id) {
			queryClient.invalidateQueries({ queryKey: queryKeys.userDetail(data.user.id) });
		}
	},
});

export const useDeleteUserMutation = (queryClient) => ({
	mutationFn: deleteUser,
	onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.users() }),
});
